import React from "react";
import { injectIntl } from "react-intl";
import MenuItem from "../menu-item/menu-item";
import List from "@material-ui/core/List";
import { Restricted, RestrictedToScopedView, permissions } from "components/auth";
import Divider from "@material-ui/core/Divider";
import Icon from "components/icon";
import MenuHelpEntry from "./menu-help-entry";
import IconMenu, { IconMenuItem } from "components/icon-menu";
import PropTypes from "prop-types";
import { useCurrentCustomerContext } from "../../../current-customer-context";

const menuItemSelected = ({ exactMatch, url }) => {
  const currentPath = window.location.pathname;

  if (exactMatch) {
    return currentPath === url;
  }

  return Boolean(currentPath.match(url));
};

const MenuWrapper = ({ iconMenu, children }) =>
  iconMenu ? (
    <IconMenu>
      {children}
      <MenuHelpEntry iconMenu />
    </IconMenu>
  ) : (
    <>
      <Divider />
      <List>
        {children}
        <MenuHelpEntry selected={menuItemSelected({ exactMatch: false, url: /\/docs/ })} />
      </List>
      <Divider />
    </>
  );

MenuWrapper.propTypes = {
  /**
   * Indica si se aplican los estilos del menú de iconos
   */
  iconMenu: PropTypes.bool,
};

const ItemWrapper = ({ iconMenu, url, text, children, disabled, selected }) => {
  const Item = iconMenu ? IconMenuItem : MenuItem;
  return (
    <Item disabled={disabled} selected={selected} url={url} text={text}>
      {children}
    </Item>
  );
};

ItemWrapper.propTypes = {
  /**
   * Indica si se aplican los estilos de desactivado
   */
  disabled: PropTypes.bool,
  /**
   * Indica si la entrada de menú está seleccionada
   */
  selected: PropTypes.bool,
  /**
   * Indica si se aplican los estilos del menú de iconos
   */
  iconMenu: PropTypes.bool,
  /**
   * Url del item
   */
  url: PropTypes.string,
  /**
   * Texto del item
   */
  text: PropTypes.string,
};

const MenuContent = ({ intl, iconMenu }) => {
  const { customers } = useCurrentCustomerContext();

  return (
    <MenuWrapper iconMenu={iconMenu}>
      <ItemWrapper
        iconMenu={iconMenu}
        selected={menuItemSelected({ exactMatch: true, url: "/" })}
        url="/"
        text={intl.formatMessage({ id: "app.menu.home" })}
      >
        <Icon name="home" />
      </ItemWrapper>
      <Restricted permissions={[permissions.event_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/event/ })}
          url="/event/list"
          text={intl.formatMessage({ id: "app.menu.event.list" })}
        >
          <Icon name="event-available" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.recording_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/recording/ })}
          url="/recording/list"
          text={intl.formatMessage({ id: "app.menu.recording.list" })}
        >
          <Icon name="camera-roll" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.asset_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/asset|^\/graphicset/ })}
          url="/asset/list"
          text={intl.formatMessage({ id: "app.menu.asset.list" })}
        >
          <Icon name="dashboard" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.deployment_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/deployment/ })}
          url="/deployment/list"
          text={intl.formatMessage({ id: "app.menu.deployment.list" })}
        >
          <Icon name="settings-input-antenna" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.source_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/source/ })}
          url="/source/list"
          text={intl.formatMessage({ id: "app.menu.source.list" })}
        >
          <Icon name="camera" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.customer_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/customer\/(?!change)/ })}
          url="/customer/list"
          text={intl.formatMessage({ id: "app.menu.customer.list" })}
        >
          <Icon name="business-center" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.user_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/user\/(?!profile)/ })}
          url="/user/list"
          text={intl.formatMessage({ id: "app.menu.user.list" })}
        >
          <Icon name="people-outline" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.contract_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          selected={menuItemSelected({ exactMatch: false, url: /^\/contract/ })}
          url="/contract/list"
          text={intl.formatMessage({ id: "app.menu.contract.list" })}
        >
          <Icon name="library-books" />
        </ItemWrapper>
      </Restricted>
      <Restricted permissions={[permissions.admin_view]}>
        <ItemWrapper
          iconMenu={iconMenu}
          disabled
          selected={menuItemSelected({ exactMatch: false, url: /^\/statistics/ })}
          url="/statistics"
          text={intl.formatMessage({ id: "app.menu.statistics" })}
        >
          <Icon name="assessment" />
        </ItemWrapper>
        <ItemWrapper
          iconMenu={iconMenu}
          disabled
          selected={menuItemSelected({ exactMatch: false, url: /^\/settings/ })}
          url="/settings"
          text={intl.formatMessage({ id: "app.menu.settings" })}
        >
          <Icon name="settings" />
        </ItemWrapper>
      </Restricted>
      <ItemWrapper
        iconMenu={iconMenu}
        selected={menuItemSelected({ exactMatch: false, url: /^\/user\/profile/ })}
        url="/user/profile"
        text={intl.formatMessage({ id: "app.menu.user" })}
      >
        <Icon name="perm-identity" />
      </ItemWrapper>
      <RestrictedToScopedView>
        {customers?.length > 1 ? (
          <ItemWrapper
            iconMenu={iconMenu}
            selected={menuItemSelected({ exactMatch: false, url: /^\/customer\/change/ })}
            url="/customer/change"
            text={intl.formatMessage({ id: "app.menu.customer.change" })}
          >
            <Icon name="people-outline" />
          </ItemWrapper>
        ) : null}
      </RestrictedToScopedView>
    </MenuWrapper>
  );
};

MenuContent.propTypes = {
  /**
   * Indica si se aplican los estilos del menú de iconos
   */
  iconMenu: PropTypes.bool,
};

export default injectIntl(MenuContent);
